import {
  As,
  Badge,
  Box,
  Button,
  ButtonProps,
  HStack,
  Icon,
  Link,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import RoundBadge from "components/Badge/RoundBadge";
import { GrWorkshop } from "react-icons/gr";
import { Link as RouterLink, useLocation } from "react-router";
import { useAnalytics } from "services/analytics";
import { SubItem } from "definitions/sidebarDefinitions/shared";

export interface BadgeProps {
  value: true | number;
  color?: string;
}

interface NavButtonWithPopoverProps extends ButtonProps {
  icon: As;
  label: string;
  route?: string;
  subtle?: boolean;
  expanded?: boolean;
  badge?: BadgeProps;
  isActive: boolean;
  subItems?: SubItem[];
}

function NavBadge({ value, color }: BadgeProps) {
  if (typeof value === "number") {
    if (color === undefined) {
      return <Text size="xs">{`(${value})`}</Text>;
    }
    return (
      <Badge variant="solid" colorScheme={color} borderRadius="8px" size="xs">
        {value}
      </Badge>
    );
  }
  return (
    <Box alignSelf="stretch" ml="1">
      <RoundBadge variant="solid" alignSelf="center" colorScheme={color} />
    </Box>
  );
}

export default function SidebarNavButtonWithPopover({
  route,
  icon,
  label,
  subtle = false,
  expanded = true,
  badge,
  isActive,
  subItems,
  ...buttonProps
}: NavButtonWithPopoverProps) {
  const activeBgColor = useColorModeValue("gray.100", "gray.700");
  const popoverBgColor = useColorModeValue("white", "gray.800");
  const popoverBorderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBgColor = useColorModeValue("gray.50", "gray.700");
  const { trackEvent } = useAnalytics();
  const location = useLocation();

  const handleClick = () => {
    trackEvent("sidebar_item.click", {
      item_name: label,
      destination: route,
      badge,
    });
  };

  const buttonContent = (
    <Button
      width="100%"
      variant="tertiary"
      justifyContent="start"
      size="xs"
      bg={isActive ? activeBgColor : undefined}
      rightIcon={expanded && badge ? <NavBadge {...badge} /> : undefined}
      {...buttonProps}
    >
      <HStack gap={expanded ? "3" : "0"} transition="gap 0.6s ease-in-out">
        {[GrWorkshop].includes(icon) ? (
          <Icon as={icon} boxSize="5" sx={{ path: { stroke: "fg.subtle" } }} />
        ) : (
          <Icon as={icon} boxSize="5" color="fg.subtle" />
        )}
        <Text
          color={subtle ? "gray.400" : undefined}
          maxWidth={expanded ? "150px" : "0px"}
          overflow="hidden"
          whiteSpace="nowrap"
          opacity={expanded ? 1 : 0}
          transition="opacity 0.6s ease-in-out, max-width 0.6s ease-in-out"
        >
          {label}
        </Text>
      </HStack>
    </Button>
  );

  // If there are sub-items, wrap in popover
  if (subItems && subItems.length > 0) {
    return (
      <Popover trigger="hover" placement="right-start" openDelay={200} closeDelay={100} gutter={8}>
        <PopoverTrigger>
          <Box width="100%">
            <Tooltip label={label} isDisabled={expanded} placement="right">
              {buttonContent}
            </Tooltip>
          </Box>
        </PopoverTrigger>
        <PopoverContent
          bg={popoverBgColor}
          borderColor={popoverBorderColor}
          boxShadow="lg"
          width="200px"
          _focus={{ boxShadow: "outline" }}
        >
          <PopoverBody p={2}>
            <VStack spacing={1} align="stretch">
              {subItems.map((subItem) => {
                const isSubItemActive = location.pathname === subItem.route;
                return (
                  <Link
                    as={RouterLink}
                    key={subItem.route}
                    to={subItem.route}
                    style={{ textDecoration: "none" }}
                    _hover={{ textDecoration: "none" }}
                    onClick={() => {
                      trackEvent("sidebar_subitem.click", {
                        parent_item: label,
                        item_name: subItem.name,
                        destination: subItem.route,
                      });
                    }}
                  >
                    <Button
                      width="100%"
                      variant="ghost"
                      justifyContent="start"
                      size="sm"
                      fontWeight={isSubItemActive ? "semibold" : "normal"}
                      bg={isSubItemActive ? activeBgColor : undefined}
                      _hover={{ bg: hoverBgColor }}
                    >
                      <HStack spacing={2}>
                        {subItem.icon && <Icon as={subItem.icon} boxSize="4" color="fg.subtle" />}
                        <Text fontSize="sm">{subItem.name}</Text>
                      </HStack>
                    </Button>
                  </Link>
                );
              })}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    );
  }

  // No sub-items, just a regular link button
  return (
    <Link
      as={RouterLink}
      to={route || "#"}
      style={{ textDecoration: "none" }}
      _hover={{ textDecoration: "none" }}
      _focus={{ boxShadow: "none" }}
      onClick={handleClick}
    >
      <Tooltip label={label} isDisabled={expanded} placement="right">
        {buttonContent}
      </Tooltip>
    </Link>
  );
}
